import React, { useEffect } from "react";
import { observer } from "mobx-react-lite";
import { makeStyles } from '@material-ui/core/styles';
import { useStores } from "../../../hooks/use-stores";
import { Link } from "react-router-dom";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { Grid, ListItemText, Typography } from "@material-ui/core";
import Divider from "@material-ui/core/Divider";
import { joinPath } from "../../../utils/helpers";
import IconButton from "@material-ui/core/IconButton";
import { ArrowForwardIos } from "@material-ui/icons";
import { useHistory, useRouteMatch } from "react-router";
import { currencyFormatter } from "../../shoppingboss/components/StyledValueWithCurrency";
import ErrorModal from "../../../components/ErrorModal";
import { starFishColor } from "../../../components/styles";
import clsx from "clsx";
import { ZELLE_DEPOSIT_DISABLED } from "../../../utils/constants";

const useStyles = makeStyles((theme) => ({

    divider: {
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(2),
    },
    addOptions: {
        gap: theme.spacing(2),
    },
    block: {
        borderRadius: 20,
        border: "1px solid #007BFF",
        display: "flex",
        padding: "10px 0 10px 15px",
        alignItems: "center",
        justifyContent: "center",
        maxHeight: 105,
        color: "white",
        flexGrow: 1,
        textDecoration: "none",
        "&:hover": {
            color: "white",
            textDecoration: "none",
        }
    },
    solidBlue:{
        ...starFishColor(theme,"backgroundColor")
    },
    disabled: {
        opacity: 0.4,
    }
}));

const AchSection = ({ url, userCountry, achDisabled, depositEligibility, handleDisabledACH }) => {
    const classes = useStyles();
    const { t } = useTranslation("wallet");
    const canInitiateDeposit = depositEligibility?.canInitiateDeposit !== false;
    const disabled = !!achDisabled || !canInitiateDeposit;

    const getForwardingLink = (url, userCountry) => {
        if (achDisabled) {
            return joinPath("/profile/help");
        } else {
            if (userCountry === 'CA') {
                return joinPath(url, "bank-ca");
            } else if (userCountry === 'AU') {
                return joinPath(url, "funds-transfer");
            } else {
                return joinPath(url, "bank");
            }
        }
    };
    const linkProps = canInitiateDeposit ? { component: Link, to: getForwardingLink(url, userCountry) } : {};

    const getListItemTitle = (userCountry) => {
        if (userCountry === 'CA') {
            return t('add_money.eTransfer');
        } else if (userCountry === 'AU') {
            return t('add_money.achAustralia');
        } else {
            return t('add_money.ach');
        }
    };

    const getListItemInfo = (userCountry) => {
        if (userCountry === 'CA') {
            return t('add_money.eTransfer_info_ca');
        } else if (userCountry === 'AU') {
            return t('add_money.eTransfer_info');
        } else {
            return t('add_money.ach_info');
        }
    };
    return (
        <Grid onClick={handleDisabledACH} item xs={12} className={clsx(classes.block, classes.solidBlue, { [classes.disabled]: disabled })} {...linkProps}>
            <ListItemText primary={getListItemTitle(userCountry)} secondary={
                <React.Fragment>
                    <Typography
                        sx={{ display: 'inline' }}
                        component="span"
                        variant="body2"
                        color="text.primary"
                    >
                        {getListItemInfo(userCountry)}
                    </Typography>
                    {(() => {
                        const info2 = ['CA', 'AU', 'US'].includes(userCountry) ? '' : t('add_money.ach_info2');
                        return info2 ? <><br /><Typography sx={{ display: 'inline' }} component="span" variant="body2" color="text.primary">{info2}</Typography></> : null;
                    })()}
                </React.Fragment>
            }
            />
            <IconButton><ArrowForwardIos /></IconButton>
        </Grid>);
};

const ZELLE_UNAVAILABLE_LABEL = 'Temporarily Unavailable';
const ZELLE_UNAVAILABLE_MESSAGE = 'Zelle from my Bank Account is temporarily unavailable. Please use ACH from your bank account or check back later.';

const ZelleSection = ({ url, depositEligibility, handleDisabledZelle }) => {
    const classes = useStyles();
    const { t } = useTranslation("wallet");
    const canInitiateDeposit = depositEligibility?.canInitiateDeposit !== false;
    const disabled = ZELLE_DEPOSIT_DISABLED || !canInitiateDeposit;
    const linkProps = !disabled ? { component: Link, to: joinPath(url, "zelle") } : {};
    const zelleUnavailableLabel = t('add_money.zelle_temporarily_unavailable', { defaultValue: ZELLE_UNAVAILABLE_LABEL });

    return (
        <Grid onClick={handleDisabledZelle} item xs={12} className={clsx(classes.block, classes.solidBlue, { [classes.disabled]: disabled })} {...linkProps}>
            <ListItemText
                primary={t('add_money.zelle')}
                secondary={ZELLE_DEPOSIT_DISABLED
                    ? (zelleUnavailableLabel === 'add_money.zelle_temporarily_unavailable' ? ZELLE_UNAVAILABLE_LABEL : zelleUnavailableLabel)
                    : t('add_money.zelle_info')}
            />
            {!ZELLE_DEPOSIT_DISABLED && <IconButton><ArrowForwardIos /></IconButton>}
        </Grid>
    );
};

export default observer(function AddEWallet({ initialHasAcceptedSparkTerms, cardStatus, achDisabled, depositEligibility }) {
    const classes = useStyles();
    const history = useHistory();
    const { t } = useTranslation("wallet");
    const match = useRouteMatch();

    const { authStore, shoppingBossStore, commonStore } = useStores();
    const { eWalletMaximumAllowed, eWalletCanAdd } = shoppingBossStore;
    const { currentUser } = authStore;
    const {
        country: userCountry,
        isStarfish,
        isSuperAppPlus,
    } = currentUser;
    const [errorMessage, setErrorMessage] = React.useState("");
    const [errorModal, setErrorModal] = React.useState(false);
    const [allowErrorModalToClose, setAllowErrorModalToClose] = React.useState(true);
    const [hasAcceptedSparkTerms, setHasAcceptedSparkTerms] = React.useState(initialHasAcceptedSparkTerms);

    useEffect(() => {
        if (!!isStarfish && !isSuperAppPlus) {
            history.push('/starfish/add_more_money');
        }
    }, [isStarfish]);

    const showErrorModal = React.useCallback((errorMessage, bAllowErrorModalToClose = true) => {
        setErrorMessage(errorMessage);
        setErrorModal(true);
        setAllowErrorModalToClose(bAllowErrorModalToClose);
    }, []);

    const hideErrorModal = () => {
        setErrorMessage("");
        setErrorModal(false);
    };


    const handleDisabledDeposit = () => {
        if (depositEligibility?.canInitiateDeposit === false) {
            commonStore.showError(depositEligibility.message || t('add_money.deposit_eligibility_error'));
        }
    };

    const handleDisabledACH = () => {
        handleDisabledDeposit();
        if (!!achDisabled) {
            commonStore.showError("We apologize, but your ACH Deposit option is currently unavailable. Please contact customer support for more details.");
        }
    };

    const handleDisabledZelle = () => {
        if (ZELLE_DEPOSIT_DISABLED) {
            const message = t('add_money.zelle_unavailable_message', { defaultValue: ZELLE_UNAVAILABLE_MESSAGE });
            commonStore.showError(message === 'add_money.zelle_unavailable_message' ? ZELLE_UNAVAILABLE_MESSAGE : message);
            return;
        }
        handleDisabledDeposit();
    };

    useEffect(() => {
        void shoppingBossStore.fetchUserBalance();
    }, []);
    return (
        <Grid container >
            <Grid item xs={12}>
                <Typography align={"center"} variant="h2">{shoppingBossStore.balance}</Typography>
            </Grid>
            <Grid item xs={12}>
                <Typography align={"center"} variant="subtitle2" color="textSecondary" disabled >
                    {t("add_money.available_balance")}
                </Typography>
            </Grid>
            <Grid item xs={12}>
                <Typography align={"center"} variant="subtitle2"  >
                    {t("add_money.maximum_allowed")}: {currencyFormatter(eWalletMaximumAllowed * 100)}
                </Typography>
            </Grid>
            <Divider className={classes.divider} />
            <Grid item xs={12}>
                <Typography align={"center"} variant="body1"  >
                    {t("add_money.can_add")}
                </Typography>
                <Typography align={"center"} variant="subtitle1" gutterBottom >
                    {currencyFormatter(eWalletCanAdd * 100)}
                </Typography>
                <Typography align={"center"} variant="subtitle1" component={"p"} gutterBottom >
                    {t("add_money.how_add")}
                </Typography>
            </Grid>
            <Grid container item xs={12} className={classes.addOptions}>
                {
                    !!["BS", "HT",].includes(userCountry)
                        ? ''
                        :
                        <>
                            <AchSection url={match.url} userCountry={userCountry} achDisabled={achDisabled} depositEligibility={depositEligibility} handleDisabledACH={handleDisabledACH} />
                        </>
                }
                {userCountry === 'US' && <ZelleSection url={match.url} depositEligibility={depositEligibility} handleDisabledZelle={handleDisabledZelle} />}
            </Grid>
            {!!errorModal
                ? (<ErrorModal allowUserToClose={allowErrorModalToClose} errorTitle={'Sorry'} handleClose={hideErrorModal} open={errorModal} error={errorMessage} />)
                : null
            }
        </Grid>

    );

});